import { useMemo, useState } from "react";
import {
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Folder,
  FolderOpen,
  User as UserIcon,
  Users,
  Wallet,
  Eye,
  Trash2,
  RotateCcw,
  Loader2,
  FoldVertical,
  UnfoldVertical,
  Mail,
  Gift,
  Ban,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { PlanRoleBadges } from "@/components/PlanRoleBadges";
import { accountLabel } from "@/lib/accountLabel";

const BILLING_BADGE = {
  trial: { cls: "bg-amber-100 text-amber-800", label: "TEST", noIcon: true },
  active: { cls: "bg-green-100 text-green-700", label: "AKTIV", noIcon: true },
  blocked: { cls: "bg-red-100 text-red-700", label: "GESPERRT", noIcon: true },
  exempt: { cls: "bg-slate-100 text-slate-600", label: "Befreit" },
};

/** Tree-Elternknoten eines Nutzers (oder null = Wurzel). */
function treeParentId(u) {
  const at = u.account_type;
  if (at === "steuerberater") return null;
  if (at === "unternehmer") return u.parent_id ? u.billing_owner_id || null : null;
  if (at === "kanzlei_mitarbeiter") return u.billing_owner_id || null;
  if (at === "mitarbeiter") return u.parent_id || null;
  return null; // einzelnutzer
}

function nodeIcon(at) {
  if (at === "steuerberater") return Folder;
  if (at === "unternehmer") return Folder;
  return UserIcon;
}

export default function AdminUserTree({
  users,
  matchesQuery,
  busyId,
  onImpersonate,
  onDeactivate,
  onReactivate,
  onDelete,
  onToggleFree,
}) {
  // 1) Baum aufbauen
  const { roots, childrenOf, byId } = useMemo(() => {
    const byId = new Map(users.map((u) => [u.id, u]));
    const childrenOf = new Map();
    const roots = [];
    for (const u of users) {
      const pid = treeParentId(u);
      if (pid && byId.has(pid)) {
        if (!childrenOf.has(pid)) childrenOf.set(pid, []);
        childrenOf.get(pid).push(u);
      } else {
        roots.push(u);
      }
    }
    const sortFn = (a, b) =>
      accountLabel(a).localeCompare(accountLabel(b), "de");
    roots.sort(sortFn);
    for (const arr of childrenOf.values()) arr.sort(sortFn);
    return { roots, childrenOf, byId };
  }, [users]);

  // 2) Roll-up-Kennzahlen je Knoten (inkl. eigener Belege + gesamter Unterbaum)
  const rollup = useMemo(() => {
    const cache = new Map();
    const compute = (u) => {
      if (cache.has(u.id)) return cache.get(u.id);
      const acc = {
        receipts: u.receipt_count || 0,
        value: u.total_amount || 0,
        mandanten: 0,
        mitarbeiter: 0,
        kanzleiMitarbeiter: 0,
      };
      for (const c of childrenOf.get(u.id) || []) {
        const sub = compute(c);
        acc.receipts += sub.receipts;
        acc.value += sub.value;
        acc.mandanten += sub.mandanten + (c.account_type === "unternehmer" ? 1 : 0);
        acc.mitarbeiter += sub.mitarbeiter + (c.account_type === "mitarbeiter" ? 1 : 0);
        acc.kanzleiMitarbeiter +=
          sub.kanzleiMitarbeiter + (c.account_type === "kanzlei_mitarbeiter" ? 1 : 0);
      }
      cache.set(u.id, acc);
      return acc;
    };
    for (const u of users) compute(u);
    return cache;
  }, [users, childrenOf]);

  // 3) Sichtbarkeit unter Filter: passt ein Knoten, wird sein GANZER Unterbaum gezeigt;
  //    zusätzlich bleiben die Vorfahren sichtbar (damit der Pfad erhalten bleibt).
  const visible = useMemo(() => {
    const set = new Set();
    const addSubtree = (u) => {
      set.add(u.id);
      for (const c of childrenOf.get(u.id) || []) addSubtree(c);
    };
    const walk = (u) => {
      if (matchesQuery(u)) {
        addSubtree(u);
        return true;
      }
      let childVisible = false;
      for (const c of childrenOf.get(u.id) || []) {
        if (walk(c)) childVisible = true;
      }
      if (childVisible) {
        set.add(u.id);
        return true;
      }
      return false;
    };
    for (const r of roots) walk(r);
    return set;
  }, [roots, childrenOf, matchesQuery]);

  // 4) Expand-State (Set aufgeklappter IDs). Standard: IMMER alles zugeklappt –
  //    Ordner werden nur durch Klick (oder „Alle aufklappen") geöffnet.
  const [expanded, setExpanded] = useState(() => new Set());

  const allFolderIds = useMemo(
    () => users.filter((u) => (childrenOf.get(u.id) || []).length > 0).map((u) => u.id),
    [users, childrenOf]
  );

  const toggle = (id) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const expandAll = () => setExpanded(new Set(allFolderIds));
  const collapseAll = () => setExpanded(new Set());

  const renderNode = (u, depth) => {
    if (!visible.has(u.id)) return null;
    const kids = (childrenOf.get(u.id) || []).filter((c) => visible.has(c.id));
    const hasKids = kids.length > 0;
    const isOpen = expanded.has(u.id);
    const r = rollup.get(u.id) || {
      receipts: 0,
      value: 0,
      mandanten: 0,
      mitarbeiter: 0,
      kanzleiMitarbeiter: 0,
    };
    // Kanzlei-Ordner: „Mitarbeiter" zählt die Kanzlei-Mitarbeiter (nicht die der Mandanten).
    const mitarbeiterCount =
      u.account_type === "steuerberater" ? r.kanzleiMitarbeiter : r.mitarbeiter;
    const isFolder = (childrenOf.get(u.id) || []).length > 0;
    const FolderIco = isOpen ? FolderOpen : Folder;
    const Icon = isFolder ? FolderIco : nodeIcon(u.account_type);
    const billing = u.is_org_payer ? BILLING_BADGE[u.billing_state] : null;
    const dimmed = !u.active;

    return (
      <div key={u.id} data-testid={`tree-node-${u.id}`}>
        <div
          className={`flex items-center gap-2 px-3 py-2.5 border-b border-border/50 transition-colors ${
            isFolder ? "bg-muted/40 hover:bg-muted" : "hover:bg-muted/50"
          } ${dimmed ? "opacity-50" : ""}`}
          style={{ paddingLeft: `${12 + depth * 22}px` }}
        >
          {/* Auf/Zuklappen */}
          {hasKids ? (
            <button
              type="button"
              onClick={() => toggle(u.id)}
              className="shrink-0 h-5 w-5 inline-flex items-center justify-center rounded hover:bg-muted text-muted-foreground"
              data-testid={`tree-toggle-${u.id}`}
              aria-expanded={isOpen}
              title={isOpen ? "Zuklappen" : "Aufklappen"}
            >
              {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          ) : (
            <span className="shrink-0 w-5" />
          )}

          <Icon className={`shrink-0 h-4 w-4 ${isFolder ? "text-primary" : "text-muted-foreground"}`} />

          {/* Identität – einzeilig (ohne Name/E-Mail-Unterzeile) */}
          <div className="min-w-0 flex-1 flex items-center gap-2 flex-wrap">
            <span className="font-medium truncate">{accountLabel(u)}</span>
            <PlanRoleBadges plan={u.plan} rolle={u.rolle} />
            {u.lifetime_free && (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[10px] font-semibold cursor-default select-none"
                data-testid={`tree-lifetime-${u.id}`}
              >
                <Gift className="h-3 w-3" />
                LifeTime
              </span>
            )}
            {!u.active && (
              <span className="rounded-full bg-red-50 text-red-600 px-2 py-0.5 text-[10px] font-semibold cursor-default select-none">
                DEAKTIV
              </span>
            )}
            {billing && (
              <span
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold cursor-default select-none ${billing.cls}`}
                data-testid={`tree-billing-${u.id}`}
              >
                {!billing.noIcon && <Wallet className="h-3 w-3" />}
                {billing.label}
              </span>
            )}
          </div>

          {/* Kennzahlen – nur Anzahl Konten/Belege (ohne Wert & letzten Scan) */}
          <div className="hidden md:block text-right text-xs text-muted-foreground shrink-0 w-[300px] whitespace-nowrap">
            {isFolder ? (
              <span data-testid={`tree-rollup-${u.id}`}>
                {r.mandanten > 0 && `${r.mandanten} Mandanten · `}
                {mitarbeiterCount} Mitarbeiter · {r.receipts} Belege
              </span>
            ) : (
              <span>{u.receipt_count} Belege</span>
            )}
          </div>

          {/* Aktionen */}
          <div className="flex items-center gap-1 shrink-0">
            <a
              href={`mailto:${u.email}`}
              title={`E-Mail an ${u.email} schreiben`}
              data-testid={`mail-${u.id}`}
              className="h-9 w-9 inline-flex items-center justify-center rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors"
            >
              <Mail className="h-4 w-4" />
            </a>
            {u.active && u.is_owner && (
              <Button
                variant="ghost"
                size="icon"
                title={u.lifetime_free ? "Kostenlos entziehen" : "Kostenlos gewähren"}
                onClick={() => onToggleFree(u)}
                disabled={busyId === u.id}
                className={
                  u.lifetime_free
                    ? "text-amber-600 hover:text-amber-600 hover:bg-amber-100"
                    : "text-primary hover:text-primary hover:bg-primary/10"
                }
                data-testid={`free-toggle-button-${u.id}`}
              >
                {u.lifetime_free ? <Ban className="h-4 w-4" /> : <Gift className="h-4 w-4" />}
              </Button>
            )}
            {u.active ? (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  title="Account ansehen / bearbeiten"
                  onClick={() => onImpersonate(u)}
                  disabled={busyId === u.id}
                  data-testid={`impersonate-button-${u.id}`}
                >
                  {busyId === u.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  title="Deaktivieren"
                  onClick={() => onDeactivate(u)}
                  disabled={busyId === u.id}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  data-testid={`deactivate-button-${u.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  title="Reaktivieren"
                  onClick={() => onReactivate(u)}
                  disabled={busyId === u.id}
                  className="text-green-700 hover:text-green-700 hover:bg-green-100"
                  data-testid={`reactivate-button-${u.id}`}
                >
                  {busyId === u.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RotateCcw className="h-4 w-4" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  title="Endgültig löschen"
                  onClick={() => onDelete(u)}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                  data-testid={`delete-user-button-${u.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        </div>

        {hasKids && isOpen && kids.map((c) => renderNode(c, depth + 1))}
      </div>
    );
  };

  // Master-Toggle: gesamte Nutzerübersicht standardmäßig eingeklappt (wie Löschprotokoll).
  const [sectionOpen, setSectionOpen] = useState(false);

  const visibleRoots = roots.filter((r) => visible.has(r.id));

  return (
    <div className="bg-white border border-border overflow-hidden" data-testid="admin-user-tree">
      <button
        type="button"
        onClick={() => setSectionOpen((o) => !o)}
        className="w-full flex items-center gap-2 text-lg font-medium text-left px-4 py-3"
        data-testid="tree-section-toggle"
        aria-expanded={sectionOpen}
      >
        <Users className="h-5 w-5 text-primary" />
        Nutzerübersicht
        <span className="text-sm font-normal text-muted-foreground">
          ({visible.size} sichtbar)
        </span>
        {sectionOpen ? (
          <ChevronUp className="h-4 w-4 ml-auto text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 ml-auto text-muted-foreground" />
        )}
      </button>

      {sectionOpen && (
        <div className="space-y-3 border-t border-border p-4">
          <div className="flex items-center justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={expandAll}
              className="rounded-none"
              data-testid="tree-expand-all"
            >
              <UnfoldVertical className="h-4 w-4 mr-1.5" /> Alle aufklappen
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={collapseAll}
              className="rounded-none"
              data-testid="tree-collapse-all"
            >
              <FoldVertical className="h-4 w-4 mr-1.5" /> Alle einklappen
            </Button>
          </div>

          <div className="bg-white border border-border overflow-hidden">
            {visibleRoots.length > 0 ? (
              visibleRoots.map((r) => renderNode(r, 0))
            ) : (
              <div className="px-4 py-10 text-center text-muted-foreground" data-testid="tree-empty">
                Keine Nutzer gefunden.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
